import React, {
  Fragment,
  useCallback,
  useContext,
  useEffect,
  useState,
} from "react";
import { makeStyles } from "@material-ui/core/styles";
import {
  any,
  dropLast,
  equals,
  includes,
  last,
  length,
  map,
  pluck,
  prop,
  uniqBy,
} from "ramda";
import { OrganizeContext } from "../organize-context";
import {
  useAddToModule,
  useDeleteModuleItems,
} from "../../modules/moduleServices";
import {
  useAddToAnthology,
  useRemoveCollectionsFromAnthology,
  useUpdateAnthologyItems,
} from "../../anthologies/anthologyServices";
import {
  useAddToCollection,
  useRemoveContentFromCollection,
} from "../../collections/collectionServices";
import {
  useCreatorModules,
  useUnorganizedContentGroups,
} from "../../creatorServices";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import {
  faAngleRight,
  faArrowLeft,
  faX,
} from "@fortawesome/pro-regular-svg-icons";
import { useSelected } from "../OrganizeProvider";
import { isBlank, notBlank } from "../../../util/ext";
import {
  IncludeDialogTypes,
  isAnthology,
  isAnthologyOrCollection,
  isCollection,
  isContent,
  groupChildrenByParent,
  getContentParentsForAnthology,
} from "../organizeUtil";
import { capitalize } from "../../../util/stringFormat";
import { getIds } from "../../../util/mediaUtil";

import Button from "@material-ui/core/Button";
import Dialog from "@material-ui/core/Dialog";
import DialogActions from "@material-ui/core/DialogActions";
import DialogContent from "@material-ui/core/DialogContent";
import DialogTitle from "@material-ui/core/DialogTitle";
import Grid from "@material-ui/core/Grid";
import IconButton from "@material-ui/core/IconButton";
import List from "@material-ui/core/List";
import ListItem from "@material-ui/core/ListItem";
import ListItemText from "@material-ui/core/ListItemText";
import ListItemSecondaryAction from "@material-ui/core/ListItemSecondaryAction";
import Typography from "@material-ui/core/Typography";

import ContentGroupsList from "./ContentGroupsList";
import ChildrenList from "./ChildrenList";
import PanelLoadingIndicator from "../../../components/PanelLoadingIndicator";
import IncludeSearchBar from "./SearchBar";
import { Tooltip } from "@material-ui/core";

const useStyles = makeStyles(theme => ({
  title: {
    color: theme.palette.primary.brandPrimary,
  },

  paper: {
    height: 500,
  },
}));

const Breadcrumbs = {
  organize: { name: "", id: "organize" },
  contentGroups: {
    name: "Organized",
    state: {
      mediaType: null,
    },
    useContentGroups: useCreatorModules,
    id: "contentGroups",
  },
  awaitingModule: {
    name: "Unorganized",
    state: {
      mediaType: "anthology,collection",
      hasParent: false,
    },
    useContentGroups: useUnorganizedContentGroups,
    id: "awaitingModule",
  },
};

const TopLevelBreadcrumbIds = ["awaitingModule", "contentGroups", "organize"];

const HomeList = ({ addBreadcrumb }) => {
  return (
    <List dense>
      <ListItem button onClick={addBreadcrumb(Breadcrumbs.contentGroups)}>
        <ListItemText>Organized</ListItemText>
        <ListItemSecondaryAction>
          <IconButton
            edge="end"
            onClick={addBreadcrumb(Breadcrumbs.contentGroups)}
          >
            <FontAwesomeIcon icon={faAngleRight} />
          </IconButton>
        </ListItemSecondaryAction>
      </ListItem>

      <ListItem button onClick={addBreadcrumb(Breadcrumbs.awaitingModule)}>
        <ListItemText>Unorganized</ListItemText>
        <ListItemSecondaryAction>
          <IconButton
            edge="end"
            onClick={addBreadcrumb(Breadcrumbs.awaitingModule)}
          >
            <FontAwesomeIcon icon={faAngleRight} />
          </IconButton>
        </ListItemSecondaryAction>
      </ListItem>
    </List>
  );
};

const ListBody = props => {
  const { breadcrumbs } = props;
  const breadcrumb = last(breadcrumbs);
  const Body = includes(breadcrumb.id, ["contentGroups", "awaitingModule"])
    ? ContentGroupsList
    : includes(breadcrumb.mediaType, ["module", "anthology", "collection"])
    ? ChildrenList
    : HomeList;

  return <Body {...props} />;
};

const Header = () => {
  const classes = useStyles();
  const { state } = useContext(OrganizeContext);
  const [title, setTitle] = useState("");
  const [otherTitles, setOtherTitles] = useState("");

  useEffect(() => {
    if (notBlank(state.includeDialog.selected)) {
      const total = length(state.includeDialog.selected);
      const firstItem = state.includeDialog.selected[0];
      setTitle(firstItem.name || firstItem.title);
      setOtherTitles(
        total > 1
          ? `and ${total - 1} other ${total - 1 === 1 ? "item" : "items"}`
          : "",
      );
    }
  }, [state]);

  return (
    notBlank(state.includeDialog.selected) && (
      <Typography variant="h2" style={{ fontSize: "2rem" }}>
        {capitalize(state.includeDialog.action)}{" "}
        <span className={classes.title}>{title}</span> {otherTitles}
      </Typography>
    )
  );
};

const isTopLevelBreadcrumb = breadcrumb =>
  includes(breadcrumb.id, TopLevelBreadcrumbIds);

const IncludeDialog = ({ SnackAlertHandler }) => {
  const classes = useStyles();
  const { state, updateIncludeDialog } = useContext(OrganizeContext);
  const { removeAllSelected } = useSelected();
  const { showAlert, SnackAlert } = SnackAlertHandler;

  //-- Manage Search
  const [query, setQuery] = useState("");

  //-- Manage Selected Group
  const [selectedGroup, setSelectedGroup] = useState({});
  const handleGroupCheckboxClick = group => event => {
    // Do not select content list items
    if (!isContent(group)) {
      setSelectedGroup(prev => {
        const breadcrumb = last(breadcrumbs);
        if (equals(prev.id, group.id)) {
          return isTopLevelBreadcrumb(breadcrumb) ? {} : breadcrumb;
        } else {
          return group;
        }
      });
    }
  };

  //-- Manage Dialog Breadcrumbs
  const [breadcrumbs, setBreadcrumbs] = useState([Breadcrumbs.organize]);
  const closeDialog = useCallback(() => {
    setBreadcrumbs([Breadcrumbs.organize]);
    setSelectedGroup({});
    updateIncludeDialog({
      open: false,
      action: "",
      activeType: null,
      selected: [],
    });
    setQuery("");
  }, [updateIncludeDialog]);

  const addBreadcrumb = breadcrumb => event => {
    setSelectedGroup(isTopLevelBreadcrumb(breadcrumb) ? {} : breadcrumb);
    setBreadcrumbs(prev => uniqBy(prop("id"), [...prev, breadcrumb]));
    setQuery("");
  };

  const handleBack = () => {
    setBreadcrumbs(prev => {
      const breadcrumbs = dropLast(1, prev);
      const breadcrumb = last(breadcrumbs);
      setSelectedGroup(isTopLevelBreadcrumb(breadcrumb) ? {} : breadcrumb);
      return breadcrumbs;
    });
    setQuery("");
  };

  const addToModule = useAddToModule();
  const deleteModuleItems = useDeleteModuleItems();
  const addToAnthology = useAddToAnthology();
  const updateAnthologyItems = useUpdateAnthologyItems();
  const removeCollectionsFromAnthology = useRemoveCollectionsFromAnthology();
  const removeContentFromCollection = useRemoveContentFromCollection();
  const addToCollection = useAddToCollection();
  const handleMove = useCallback(async () => {
    try {
      const { action, activeType, selected } = state.includeDialog;

      if (equals(action, "move")) {
        const prevParents = groupChildrenByParent(selected);

        // Remove children from previous parents
        for (const { prevParent, newChildren } of prevParents) {
          if (notBlank(prevParent.id)) {
            if (equals(prevParent.mediaType, "collection")) {
              await removeContentFromCollection.mutateAsync({
                collectionId: prevParent.id,
                contentIds: newChildren.ids,
              });
            }

            if (equals(prevParent.mediaType, "anthology")) {
              // Remove content from all collections in anthology
              if (equals(activeType, IncludeDialogTypes.content)) {
                const collections = getContentParentsForAnthology(
                  selected,
                  prevParent,
                );

                await Promise.all(
                  map(
                    collectionId =>
                      removeContentFromCollection.mutateAsync({
                        collectionId,
                        contentIds: getIds(selected),
                      }),
                    getIds(collections),
                  ),
                );
              } else {
                await removeCollectionsFromAnthology.mutateAsync({
                  anthologyId: prevParent.id,
                  mediaIds: newChildren.ids,
                });
              }
            }

            if (equals(prevParent.mediaType, "module")) {
              await deleteModuleItems.mutateAsync({
                id: prevParent.id,
                ids: newChildren.ids,
              });
            }
          }
        }
      }

      const contentMediaIds = pluck("id", selected);

      const addToGroup = {
        module: addToModule,
        anthology: addToAnthology,
        collection: addToCollection,
      };

      await addToGroup[selectedGroup.mediaType].mutateAsync({
        resourceIds: {
          id: selectedGroup.id,
        },
        data: {
          ids: contentMediaIds,
        },
      });

      showAlert("success", [
        `${length(selected)} ${
          length(selected) === 1 ? "Item" : "Items"
        } Successfully ${action === "copy" ? "Copied" : "Moved"} To ${
          selectedGroup.name
        }`,
      ]);

      removeAllSelected();
      closeDialog();
    } catch (err) {
      showAlert("error", [err.message]);
    }
  }, [
    addToModule,
    addToAnthology,
    addToCollection,
    closeDialog,
    deleteModuleItems,
    removeAllSelected,
    removeCollectionsFromAnthology,
    removeContentFromCollection,
    selectedGroup,
    showAlert,
    state.includeDialog,
  ]);

  const disableMove = useCallback(() => {
    const breadcrumb = last(breadcrumbs);
    const selected = state.includeDialog.selected;
    const action = state.includeDialog.action;

    if (isTopLevelBreadcrumb(breadcrumb)) {
      return isBlank(selectedGroup);
    }

    if (any(isAnthology, selected)) {
      return isAnthologyOrCollection(selectedGroup);
    }

    if (any(isCollection, selected)) {
      if (action === "copy") {
        return isAnthologyOrCollection(selectedGroup);
      } else {
        return isCollection(selectedGroup);
      }
    }

    if (any(isContent(selected))) {
      return isAnthology(selectedGroup);
    }
  }, [
    breadcrumbs,
    selectedGroup,
    state.includeDialog.action,
    state.includeDialog.selected,
  ]);

  return (
    <Fragment>
      <SnackAlert />

      <Dialog
        maxWidth="lg"
        open={state.includeDialog.open}
        classes={{
          paper: classes.paper,
        }}
      >
        {addToModule.isLoading ||
        addToAnthology.isLoading ||
        addToCollection.isLoading ||
        deleteModuleItems.isLoading ||
        updateAnthologyItems.isLoading ? (
          <PanelLoadingIndicator transparent />
        ) : null}

        <DialogTitle disableTypography style={{ paddingBottom: 0 }}>
          <Grid container justify="space-between" alignItems="center">
            <Grid item xs>
              <Header />
            </Grid>

            <Grid item xs="auto">
              <IconButton aria-label="Close dialog" onClick={closeDialog}>
                <FontAwesomeIcon icon={faX} />
              </IconButton>
            </Grid>
          </Grid>

          {breadcrumbs.length > 1 && (
            <Grid
              container
              alignItems="center"
              justify="space-between"
              style={{ height: 60 }}
            >
              <Grid container item xs alignItems="center">
                {breadcrumbs.length > 1 && (
                  <Grid item>
                    <IconButton onClick={handleBack}>
                      <FontAwesomeIcon icon={faArrowLeft} />
                    </IconButton>
                  </Grid>
                )}
                <Grid item xs={10}>
                  <Tooltip title={last(breadcrumbs).name}>
                    <Typography variant="h2" noWrap>
                      {last(breadcrumbs).name}
                    </Typography>
                  </Tooltip>
                </Grid>
              </Grid>

              {breadcrumbs.length > 1 && (
                <Grid item xs>
                  <IncludeSearchBar query={query} onQueryChange={setQuery} />
                </Grid>
              )}
            </Grid>
          )}
        </DialogTitle>

        <DialogContent style={{ paddingTop: 0 }}>
          <ListBody
            breadcrumbs={breadcrumbs}
            selectedGroup={selectedGroup}
            handleGroupCheckboxClick={handleGroupCheckboxClick}
            setBreadcrumbs={setBreadcrumbs}
            addBreadcrumb={addBreadcrumb}
            query={query}
          />
        </DialogContent>

        <DialogActions>
          <Grid container justify="flex-end" alignItems="center">
            <Grid item>
              <Button onClick={closeDialog} color="primary">
                Cancel
              </Button>
            </Grid>

            <Grid item>
              <Button
                disabled={disableMove()}
                color="primary"
                variant="contained"
                onClick={handleMove}
              >
                {capitalize(state.includeDialog.action)}
              </Button>
            </Grid>
          </Grid>
        </DialogActions>
      </Dialog>
    </Fragment>
  );
};

export default IncludeDialog;
